// AgencyDetails.jsx
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Tabs,
  Tab,
  Form,
  Modal,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaCheckCircle,
  FaEdit,
  FaDownload,
  FaArrowLeft,
  FaFilePdf,
  FaFileWord,
  FaFileImage,
  FaFileAlt,
} from "react-icons/fa";
import { FiXCircle } from "react-icons/fi";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Baseurl } from "../../baseurl";
import { toast } from "react-toastify";

const AgencyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReworkModal, setShowReworkModal] = useState(false);
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [documentType, setDocumentType] = useState("");
  const [remarks, setRemarks] = useState("");
  const [validUpTo, setValidUpTo] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAgencyDetails();
  }, [id]);

  const fetchAgencyDetails = async () => {
    try {
      // Replace with your API call
      const response = await axios.get(`${Baseurl}/api/agency/details/${id}`);
      if (response.data.success) {
        setAgency(response.data.data);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching agency details:", error);
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      Pending: "warning",
      Approved: "success",
      Rework: "info",
      Rejected: "danger",
    };
    return (
      <Badge bg={variants[status]} className="fs-6">
        {status}
      </Badge>
    );
  };

  const handleViewDocument = (docUrl, docType) => {
    setCurrentDocument(docUrl);
    setDocumentType(docType);
    setShowViewerModal(true);
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return <FaFileAlt className="me-2" />;

    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "pdf":
        return <FaFilePdf className="me-2 text-danger" />;
      case "doc":
      case "docx":
        return <FaFileWord className="me-2 text-primary" />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <FaFileImage className="me-2 text-success" />;
      default:
        return <FaFileAlt className="me-2" />;
    }
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return "Document";
    return url.split("/").pop() || "Document";
  };

  const isImageFile = (fileName) => {
    if (!fileName) return false;
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
    const extension = fileName.split(".").pop().toLowerCase();
    return imageExtensions.includes(extension);
  };

  const isPdfFile = (fileName) => {
    if (!fileName) return false;
    return fileName.toLowerCase().endsWith(".pdf");
  };

  const downloadDocument = (docType, fileName) => {
    // Implement actual download logic
    console.log(`Downloading ${docType}: ${fileName}`);
    // For now, we'll open in new tab for download
    window.open(fileName, "_blank");
  };
  // In your modal submit handlers (Approve/Reject/Rework)
  const sendNotificationToAgency = async (msg) => {
    try {
      const response = await axios.post(`${Baseurl}/api/agency/send-message`, {
        name: agency.name,
        companyName: agency.companyDetails.companyName,
        email: agency.email,
        agencystatus: msg, // or 'Rejected', 'Rework'
        remark: remarks,
        applicationId: agency.applicationID,
      });
      toast.info(response.data.message);
    } catch (error) {
      console.error("Error sending notification:", error);
      alert(error.response?.data?.message || "Failed to send notification");
    }
  };

  // Call this function after approving/rejecting/sending for rework
  const handleApprove = async () => {
    setActionLoading(true);
    try {
      // API call to approve
      await axios.put(`${Baseurl}/api/agency/update/${id}`, {
        validUpTo,
        agencyStatus: "Approved",
        remarks,
      });
      toast.success("Agency approved successfully!");
      sendNotificationToAgency("Approved");
      navigate("/agencies/pendingList");
    } catch (error) {
      alert("Error approving agency");
    } finally {
      setActionLoading(false);
      setShowApproveModal(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${Baseurl}/api/agency/update/${id}`, {
        remarks,
        agencyStatus: "Rejected",
      });
      toast.info("Agency rejected!");
      sendNotificationToAgency("Rejected");
      navigate("/agencies/pendingList");
    } catch (error) {
      alert("Error rejecting agency");
    } finally {
      setActionLoading(false);
      setShowRejectModal(false);
    }
  };

  const handleRework = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${Baseurl}/api/agency/update/${id}`, {
        remarks,
        agencyStatus: "Rework",
      });
      toast.error("Sent for rework!");
      sendNotificationToAgency("Rework");
      navigate("/agencies/pendingList");
    } catch (error) {
      alert("Error sending for rework");
    } finally {
      setActionLoading(false);
      setShowReworkModal(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!agency) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">Agency not found</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="text-primary">Agency Application Details</h3>
              <p className="text-muted mb-0">
                Application ID: <strong>{agency.applicationID}</strong>
                <span className="ms-3">
                  Status: {getStatusBadge(agency.agencyStatus)}
                </span>
              </p>
            </div>
            <div>
              {agency.agencyStatus !== "Approved" && (
                <Button
                  variant="success"
                  className="me-2"
                  onClick={() => setShowApproveModal(true)}
                  disabled={actionLoading}
                >
                  <FaCheckCircle className="me-2" /> Approve
                </Button>
              )}
              <Button
                variant="info"
                className="me-2"
                onClick={() => setShowReworkModal(true)}
                disabled={actionLoading}
              >
                <FaEdit className="me-2" /> Send for Rework
              </Button>
              <Button
                variant="danger"
                onClick={() => setShowRejectModal(true)}
                disabled={actionLoading}
              >
                <FiXCircle className="me-2" /> Reject
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Main Content */}
      <Row>
        <Col lg={12}>
          <Tabs defaultActiveKey="personal" className="mb-3">
            {/* Personal Details Tab */}
            <Tab eventKey="personal" title="Personal Details">
              <Card className="shadow-sm">
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <h5 className="mb-3 border-bottom pb-2">
                        Basic Information
                      </h5>
                      <table className="table table-borderless">
                        <tbody>
                          <tr>
                            <td width="40%">
                              <strong>Full Name:</strong>
                            </td>
                            <td>{agency.name}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Father's Name:</strong>
                            </td>
                            <td>{agency.fatherName}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Date of Birth:</strong>
                            </td>
                            <td>{new Date(agency.dOB).toLocaleDateString()}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Mobile No:</strong>
                            </td>
                            <td>{agency.mobileNo}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Email:</strong>
                            </td>
                            <td>{agency.email}</td>
                          </tr>
                        </tbody>
                      </table>
                    </Col>
                    <Col md={6}>
                      <h5 className="mb-3 border-bottom pb-2">Documents</h5>
                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                          <div className="d-flex align-items-center">
                            {getFileIcon(agency.personalDetails.panCard)}
                            <span>PAN Card</span>
                          </div>
                          <div className="d-flex gap-2">
                            {agency.personalDetails.panCard}
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                          <div className="d-flex align-items-center">
                            {getFileIcon(
                              agency.personalDetails.supportingDocument,
                            )}
                            <span>Supporting Document</span>
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                handleViewDocument(
                                  agency.personalDetails.supportingDocument,
                                  "Supporting Document",
                                )
                              }
                            >
                              View
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                downloadDocument(
                                  "Supporting",
                                  agency.personalDetails.supportingDocument,
                                )
                              }
                            >
                              <FaDownload />
                            </Button>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                          <div className="d-flex align-items-center">
                            {getFileIcon(agency.personalDetails.passPhoto)}
                            <span>Passport Photo</span>
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                handleViewDocument(
                                  agency.personalDetails.passPhoto,
                                  "Passport Photo",
                                )
                              }
                            >
                              View
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                downloadDocument(
                                  "Photo",
                                  agency.personalDetails.passPhoto,
                                )
                              }
                            >
                              <FaDownload />
                            </Button>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                          <div className="d-flex align-items-center">
                            {getFileIcon(agency.logo)}
                            <span>Organisation Logo</span>
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                handleViewDocument(
                                  agency.logo,
                                  "Organisation Logo",
                                )
                              }
                            >
                              View
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                downloadDocument(
                                  "Organisation Logo",
                                  agency.logo,
                                )
                              }
                            >
                              <FaDownload />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>

            {/* Company Details Tab */}
            <Tab eventKey="company" title="Company Details">
              <Card className="shadow-sm">
                <Card.Body>
                  <Row>
                    <Col md={8}>
                      <h5 className="mb-3 border-bottom pb-2">
                        Company Information
                      </h5>
                      <table className="table table-borderless">
                        <tbody>
                          <tr>
                            <td width="40%">
                              <strong>Company Name:</strong>
                            </td>
                            <td>{agency.companyDetails.companyName}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Registered Address:</strong>
                            </td>
                            <td>{agency.companyDetails.registeredAddress}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Contact Number:</strong>
                            </td>
                            <td>{agency.companyDetails.contactNumber}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Email:</strong>
                            </td>
                            <td>{agency.companyDetails.email}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>GST Number:</strong>
                            </td>
                            <td>{agency.companyDetails.gst}</td>
                          </tr>
                        </tbody>
                      </table>
                    </Col>
                    <Col md={4}>
                      <h5 className="mb-3 border-bottom pb-2">
                        Company Documents
                      </h5>
                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                          <div className="d-flex align-items-center">
                            {getFileIcon(agency.documents.companyPAN)}
                            <span>Company PAN</span>
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                handleViewDocument(
                                  agency.documents.companyPAN,
                                  "Company PAN",
                                )
                              }
                            >
                              View
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                downloadDocument(
                                  "Company PAN",
                                  agency.documents.companyPAN,
                                )
                              }
                            >
                              <FaDownload />
                            </Button>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                          <div className="d-flex align-items-center">
                            {getFileIcon(agency.documents.officeAddressProof)}
                            <span>Office Address Proof</span>
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                handleViewDocument(
                                  agency.documents.officeAddressProof,
                                  "Office Address Proof",
                                )
                              }
                            >
                              View
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                downloadDocument(
                                  "Address Proof",
                                  agency.documents.officeAddressProof,
                                )
                              }
                            >
                              <FaDownload />
                            </Button>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                          <div className="d-flex align-items-center">
                            {getFileIcon(agency.documents.relationTypeProof)}
                            <span>Relation Type Proof</span>
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                handleViewDocument(
                                  agency.documents.relationTypeProof,
                                  "Relation Type Proof",
                                )
                              }
                            >
                              View
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                downloadDocument(
                                  "Relation Proof",
                                  agency.documents.relationTypeProof,
                                )
                              }
                            >
                              <FaDownload />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>

            {/* Compliance Tab */}
            <Tab eventKey="compliance" title="Compliance">
              <Card className="shadow-sm">
                <Card.Body>
                  <Row>
                    <Col md={12}>
                      <h5 className="mb-3 border-bottom pb-2">
                        Compliance Documents
                      </h5>
                      <Row>
                        <Col md={6}>
                          <div className="p-3 border rounded mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="mb-0">DRA Certificate</h6>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    handleViewDocument(
                                      agency.compliance.DRACertificate,
                                      "DRA Certificate",
                                    )
                                  }
                                >
                                  View
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() =>
                                    downloadDocument(
                                      "DRA Certificate",
                                      agency.compliance.DRACertificate,
                                    )
                                  }
                                >
                                  <FaDownload />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="p-3 border rounded mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="mb-0">
                                Police Verification Report
                              </h6>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    handleViewDocument(
                                      agency.compliance
                                        .policeVerificationReport,
                                      "Police Verification Report",
                                    )
                                  }
                                >
                                  View
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() =>
                                    downloadDocument(
                                      "Police Report",
                                      agency.compliance
                                        .policeVerificationReport,
                                    )
                                  }
                                >
                                  <FaDownload />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </Col>
      </Row>

      {/* Document Viewer Modal */}
      <Modal
        show={showViewerModal}
        onHide={() => setShowViewerModal(false)}
        size="lg"
        fullscreen="lg-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {getFileIcon(currentDocument)}
            {documentType} - {getFileNameFromUrl(currentDocument)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ minHeight: "70vh" }}>
          <div className="d-flex justify-content-center align-items-center h-100">
            {currentDocument ? (
              isImageFile(currentDocument) ? (
                <img
                  src={currentDocument}
                  alt={documentType}
                  className="img-fluid"
                  style={{ maxHeight: "70vh", objectFit: "contain" }}
                />
              ) : isPdfFile(currentDocument) ? (
                <iframe
                  src={currentDocument}
                  title={documentType}
                  width="100%"
                  height="600px"
                  style={{ border: "none" }}
                />
              ) : (
                <div className="text-center p-5">
                  <FaFileAlt size={64} className="text-muted mb-3" />
                  <h5>Preview not available</h5>
                  <p className="text-muted">
                    This document type cannot be previewed. Please download to
                    view.
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => {
                      console.log(currentDocument, "currentDocument");
                      //   window.open(currentDocument, "_blank");
                      //   setShowViewerModal(false);
                    }}
                  >
                    <FaDownload className="me-2" /> Download Document
                  </Button>
                </div>
              )
            ) : (
              <div className="text-center p-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading document...</p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewerModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              window.open(currentDocument, "_blank");
              setShowViewerModal(false);
            }}
          >
            <FaDownload className="me-2" /> Download
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approve Modal */}
      <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Approve Agency Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Valid Up To</Form.Label>
              <Form.Control
                type="date"
                value={validUpTo}
                onChange={(e) => setValidUpTo(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Remarks (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add any remarks for approval..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowApproveModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleApprove}
            disabled={!validUpTo || actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : "Approve Application"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reject Agency Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            Are you sure you want to reject this application? This action cannot
            be undone.
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>Reason for Rejection *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Please specify the reason for rejection..."
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={!remarks.trim() || actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : "Confirm Reject"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Rework Modal */}
      <Modal show={showReworkModal} onHide={() => setShowReworkModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Send for Rework</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Instructions for Rework *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Specify what needs to be corrected or updated..."
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReworkModal(false)}>
            Cancel
          </Button>
          <Button
            variant="info"
            onClick={handleRework}
            disabled={!remarks.trim() || actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : "Send for Rework"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AgencyDetails;
